/**
 * RESPONSIBILITY: the @Img column of a @List row.
 *
 * Its own file because it is the one column that talks to the server, and upload state
 * and upload failure belong with the thing that owns them.
 *
 * The picture is the button, exactly as in the full-size control: the file input covers
 * it at zero opacity so the click target is the image and the cell stays
 * keyboard-reachable. `display:none` would take it out of the tab order entirely.
 */
import { useState } from 'react';
import { EMPTY_IMAGE, hasImage, type ImageValue } from '@framework/core';
import { uploadMedia } from '../upload-media';
import { useMagnify, MAX_ZOOM } from './magnify';
import type { Cell } from './cell-types';

const ImageCell: Cell['render'] = ({ field, value, onChange, id }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const zoom = useMagnify();
  const img: ImageValue = { ...EMPTY_IMAGE, ...(value as Partial<ImageValue> | undefined) };

  async function choose(file: File) {
    setBusy(true); setErr(null);
    const r = await uploadMedia(file, field.folder ?? 'misc', field.maxSizeMb ?? 4, 'image');
    setBusy(false);
    // The row carries the reference, so reordering moves the picture with the row.
    // Alt text is the editor's, not the server's — keep it across a replacement.
    if (r.ok) onChange({ path: r.value.path, alt: img.alt });
    else setErr(r.error);
  }

  return (
    <div className="ctl-cell-img">
      <label
        ref={zoom.box} style={zoom.style}
        className={zoom.big ? 'ctl-img-pick ctl-img-sm is-big' : 'ctl-img-pick ctl-img-sm'}
        title={hasImage(img) ? 'Click to replace' : 'Click to choose'}
      >
        {hasImage(img)
          ? <img src={img.path} alt={img.alt} className="ctl-img-preview" />
          : <span className="ctl-img-empty">none</span>}
        <span className="ctl-img-veil">{busy ? '…' : hasImage(img) ? 'Replace' : 'Choose'}</span>
        <input
          id={id}
          type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
          disabled={busy}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); }}
        />
      </label>

      {/* Same advice the full-size control gives, in the space a cell has: a picture in a
          row is chosen the same way and wants the same shape. */}
      {field.size && <span className="ctl-size ctl-cell-size">{field.size}</span>}

      {/* The same field the full-size control has. Without it a picture that lives in a
          list could never be described at all — the row carried alt text forward across a
          replacement and offered nowhere to type it. */}
      <input
        type="text" className="ctl-input ctl-cell-alt"
        placeholder="Alt text" aria-label={`${field.label} — alt text`}
        value={img.alt}
        onChange={(e) => onChange({ ...img, alt: e.target.value })}
      />

      {hasImage(img) && (
        <>
          {/* A cell's thumbnail is smaller than the control's, so this is needed more
              here, not less. */}
          <button
            type="button" className="ctl-img-icon ctl-img-zoom"
            aria-pressed={zoom.big} onClick={zoom.toggle}
            title={zoom.big ? 'Back to normal size' : `Magnify (${MAX_ZOOM}×)`}
            aria-label={zoom.big ? 'Back to normal size' : 'Magnify'}
          >{zoom.big ? '⊖' : '⊕'}</button>
          {/* The full-size control has had this and a cell had not, so every picture that
              lives in a LIST — every ministry, every event, every person — was one you
              could replace and never get back out. The rest of the row's icons were here;
              this one was simply missed. No filename to offer, as the control has after
              an upload, so the browser uses the one in the address — but `download` must
              still be there, or the link opens the picture in a tab instead of saving it. */}
          <a
            className="ctl-img-icon ctl-img-get" href={`${img.path}?original`} download
            title="Download" aria-label="Download image"
          >↓</a>
          <button
            type="button" className="ctl-img-icon ctl-img-drop"
            onClick={() => onChange({ ...EMPTY_IMAGE })}
            title="Remove" aria-label="Remove image"
          >✕</button>
        </>
      )}

      {err && <span className="ctl-error ctl-cell-err">{err}</span>}
    </div>
  );
};

// Wide enough for the picker and a usable alt-text field beside it.
export const imageCell: Cell = { render: ImageCell, basis: () => '26ch' };
